import { Component, inject, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { Subscription } from 'rxjs';
import {
  ModalAbstractComponent,
  SelectModalBaseComponent
} from '@bling-fe/shared/modals/base-modal';
import { DocumentCategory } from '../../core/declarations/document-category.interface';
import { DocumentCategoryRepository } from '../../shared/repositories/document-category.repository';

export interface MoveToFolderModalProps {
  /** What is moved, for the subtitle. */
  name: string;
  originType?: string;
  originId?: number;
  /** Where it lies now; null is the top level. */
  currentId: number | null;
  /** A folder cannot go into itself or below itself. */
  movingFolderId?: number;
  /** The "Mitwirkung" view or the normal one: a move stays in its view. */
  participation?: boolean;
}

export interface MoveToFolderModalResult {
  parentId: number | null;
}

type Level = { id: number | null; name: string };

// One level at a time: open a folder to go down, the back arrow to go up, the
// button moves into the level on screen.
@Component({
  selector: 'fl-move-to-folder-modal',
  templateUrl: './move-to-folder-modal.component.html',
  styleUrls: ['./move-to-folder-modal.component.scss'],
  imports: [
    IonicModule,
    TranslateModule,
    SelectModalBaseComponent,
    SvgIconComponent,
    NgIf,
    NgFor
  ]
})
export class MoveToFolderModalComponent
  extends ModalAbstractComponent<
    MoveToFolderModalProps,
    MoveToFolderModalResult
  >
  implements OnInit
{
  private repository = inject(DocumentCategoryRepository);
  private translate = inject(TranslateService);

  path: Level[] = [];
  folders?: DocumentCategory[];
  private request?: Subscription;

  get here(): Level {
    return this.path[this.path.length - 1];
  }

  get isCurrent(): boolean {
    return this.here.id === this.props.currentId;
  }

  ngOnInit() {
    this.show([
      { id: null, name: this.translate.instant('PAGES.DOCUMENTS.TITLE') }
    ]);
  }

  enter(folder: DocumentCategory) {
    this.show([...this.path, { id: folder.id, name: folder.name }]);
  }

  up() {
    this.show(this.path.slice(0, -1));
  }

  move() {
    this.dismiss({ parentId: this.here.id });
  }

  private show(path: Level[]) {
    this.path = path;
    this.folders = undefined;
    // A level left before it loaded must not land on the one shown now.
    this.request?.unsubscribe();
    this.request = this.repository
      .findAll({
        ...(this.props.originType && {
          origin_type: this.props.originType,
          origin_id: this.props.originId
        }),
        ...(this.here.id && { parent_id: this.here.id }),
        show_in_participation_page: !!this.props.participation,
        sort: 'title_asc'
      })
      .subscribe(folders => (this.folders = folders));
  }
}
